import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { CreateReportSchema } from './use-create-report';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const UpdateReportSchema = z.object({
  id: z.string(),
  name: CreateReportSchema.shape.name.optional(),
  emails: CreateReportSchema.shape.emails.optional(),
  periodicity: CreateReportSchema.shape.periodicity.optional(),
});

type UpdateReportVariables = z.infer<typeof UpdateReportSchema>;

export async function updateReportFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateReportVariables,
) {
  const instance = await axiosInstance;
  return instance.put(`/reports/export_tasks/${data.id}`, data);
}

export const useUpdateReport = () => {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateReportVariables>({
    mutationFn: variables => updateReportFn(axiosInstance, variables),
    onSuccess: (_response, { id }) => {
      client.invalidateQueries({
        queryKey: ['reports'],
      });
      client.invalidateQueries({
        queryKey: ['report', id],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateReportVariables,
    'updateReport'
  >('updateReport', mutation);
};
